/**
 * Boolean Field Type
 *
 * Provides configuration and renderers for boolean (True/False) fields:
 * - Read-only column renderer (chip display)
 * - Editable column renderer (chip with dropdown)
 * - Type registry configuration
 * - Form field renderer
 */

import type { ColumnDef, CellContext } from '@tanstack/react-table';
import { ComboBox, Chip } from 'components';
import type { ComboBoxOption } from 'components/inputs/types';
import { CaretDown } from '@phosphor-icons/react';
import { ChipCell, TableTooltip } from '../../components';
import type {
  FieldTypeConfig,
  FormFieldProps,
  ColumnRenderer,
  RenderOptions,
  CustomColumn,
  TypeConfig,
} from '../../types';
import { validateFieldValue } from '../../validation';
import { FieldError, shouldShowFieldError } from './fieldError';
import { S } from './columnWidths';
import { BooleanColors } from './columnColors';

// Import shared cell value utility
import { getCellBooleanValue } from './cellValue';

// ============================================================================
// Type Registry Configuration
// ============================================================================

/**
 * Boolean type configuration for the type registry
 * Displays True/False for boolean values
 */
export const booleanTypeConfig: TypeConfig = {
  options: [
    { id: true, label: 'Yes' },
    { id: false, label: 'No' },
  ] as const,
  chipColors: BooleanColors,
};

// ============================================================================
// Form Field Renderer
// ============================================================================

// Store reference to getTypeConfig to avoid circular dependencies
let getTypeConfigFn: ((type: string) => TypeConfig | undefined) | null = null;

/**
 * Set the getTypeConfig function (called from index.ts to avoid circular deps)
 */
export const setGetTypeConfig = (fn: (type: string) => TypeConfig | undefined): void => {
  getTypeConfigFn = fn;
};

/**
 * Render a boolean (True/False) field for forms
 */
export const renderBooleanField = ({
  col,
  value,
  valueForValidation,
  onChange,
  disabled,
  bulkEditContext,
  validationContext,
}: FormFieldProps): React.ReactNode => {
  const typeConfig = getTypeConfigFn ? getTypeConfigFn('boolean') : booleanTypeConfig;
  if (!typeConfig) {
    throw new Error(`Type config not found for type: boolean`);
  }

  const emptyDisplayNode = (col as { emptyDisplayNode?: React.ReactNode }).emptyDisplayNode;
  const valueEmpty = value === undefined || value === '' || value === null;
  const showEmptyChip = valueEmpty && emptyDisplayNode;

  // Convert boolean options to ComboBox-compatible format
  const comboBoxOptions: ComboBoxOption[] = typeConfig.options.map(o => ({
    id: String(o.id),
    label: o.label,
  }));

  // Find the matching option object for the current value
  const currentValueStr = value !== undefined && value !== '' ? String(value) : '';
  const selectedOption = comboBoxOptions.find(o => o.id === currentValueStr) || null;

  // Use Zod validation to get error message (valueForValidation for bulk-edit "keep current")
  const valueToValidate = valueForValidation !== undefined ? valueForValidation : value;
  const validation = validateFieldValue({ ...col, type: 'boolean' }, valueToValidate);
  const errorMessage = !validation.success ? validation.error : undefined;
  const showError = shouldShowFieldError(col.key, validationContext);

  const handleChange = (selectedValue: ComboBoxOption | null) => {
    if (selectedValue && typeof selectedValue === 'object' && 'id' in selectedValue) {
      const boolValue = selectedValue.id === 'true';
      onChange(col.key, boolValue);
    } else {
      onChange(col.key, undefined);
    }
    validationContext?.onFieldTouched?.(col.key);
  };

  return (
    <div key={col.key}>
      <ComboBox
        id={col.key}
        label={col.label}
        placeholder={showEmptyChip ? '' : col.placeholder}
        disabled={disabled}
        inputVariant="outlined"
        labelSeparate
        forcePopupIcon
        popupIcon={<CaretDown size={20} />}
        freeSolo={false}
        options={comboBoxOptions}
        value={selectedOption}
        onChange={(_, selectedValue, reason) => {
          if (reason === 'clear' && bulkEditContext?.isBulkEdit) {
            bulkEditContext.onKeepCurrent();
            return;
          }
          handleChange(selectedValue as ComboBoxOption | null);
        }}
        InputProps={showEmptyChip ? { startAdornment: emptyDisplayNode } : undefined}
      />
      <FieldError error={errorMessage} show={showError} />
    </div>
  );
};

// ============================================================================
// Read-Only Column Renderer
// ============================================================================

// Store reference to resolveFieldConfig to avoid circular dependencies
let resolveFieldConfigFn: ((col: { id: string; type?: string }) => unknown) | null = null;

/**
 * Set the resolveFieldConfig function (called from index.ts to avoid circular deps)
 */
export const setResolveFieldConfig = (
  fn: (col: { id: string; type?: string }) => unknown
): void => {
  resolveFieldConfigFn = fn;
};

/**
 * Helper to get boolean config
 */
const getBooleanConfig = (
  colId: string
): { options: TypeConfig['options']; chipColors: TypeConfig['chipColors'] } => {
  const resolved = resolveFieldConfigFn
    ? (resolveFieldConfigFn({ id: colId, type: 'boolean' }) as {
        kind: string;
        options: TypeConfig['options'];
        chipColors: TypeConfig['chipColors'];
      } | null)
    : {
        kind: 'boolean',
        options: booleanTypeConfig.options,
        chipColors: booleanTypeConfig.chipColors,
      };

  if (!resolved || resolved.kind !== 'boolean') {
    throw new Error(`Failed to resolve boolean config for column: ${colId}`);
  }

  return resolved;
};

/**
 * Boolean read-only renderer - displays True/False chip
 * Uses col.id as the data field name
 */
export const booleanReadOnlyRenderer = <T extends { id: number | string }>(
  col: CustomColumn<T>,
  _options: RenderOptions<T>
): Partial<ColumnDef<T>> => {
  const { options, chipColors } = getBooleanConfig(col.id);

  return {
    cell: (context: CellContext<T, unknown>) => {
      // Use getCellBooleanValue to respect accessorFn (important for custom fields)
      const value = getCellBooleanValue(context, col.id);
      const option = options.find(o => o.id === value) || options[1];
      const background = value ? chipColors.true : chipColors.false;

      return (
        <TableTooltip title={option.label}>
          <Chip
            sx={{ padding: '4px 16px' }}
            isBordered={false}
            isBackgroundFilled
            isTooltip={false}
            label={option.label}
            color={background}
            background={background}
          />
        </TableTooltip>
      );
    },
  };
};

// ============================================================================
// Editable Column Renderer
// ============================================================================

/**
 * Boolean editable renderer - displays chip with dropdown for editing
 * Uses col.id as the data field name
 */
export const booleanEditableRenderer = <T extends { id: number | string }>(
  col: CustomColumn<T>,
  _options: RenderOptions<T>
): Partial<ColumnDef<T>> => {
  const { options: booleanOptions, chipColors } = getBooleanConfig(col.id);
  const comboBoxOptions = booleanOptions.map(o => ({
    id: String(o.id),
    label: o.label,
  }));

  return {
    cell: (context: CellContext<T, unknown>) => {
      const value = getCellBooleanValue(context, col.id);
      const option = booleanOptions.find(o => o.id === value) || booleanOptions[1];
      const background = value ? chipColors.true : chipColors.false;

      return (
        <ChipCell
          context={context}
          label={option.label}
          background={background}
          color={background}
          editable
          options={comboBoxOptions}
          onUpdate={selected => {
            if (col.onUpdate) {
              const boolValue = selected.id === 'true';
              col.onUpdate(context.row.original, { [col.id]: boolValue } as Partial<T>);
            }
          }}
        />
      );
    },
    meta: {
      editable: true,
    },
  };
};

// ============================================================================
// Field Type Configuration
// ============================================================================

/**
 * Boolean field type configuration
 */
export const booleanFieldType: FieldTypeConfig = {
  placeholder: (label: string) => `Select ${label.toLowerCase()}...`,
  ...S,
};

// ============================================================================
// Typed Renderer Exports (for registry)
// ============================================================================

/** Read-only renderer with proper typing */
export const booleanReadOnly: ColumnRenderer = booleanReadOnlyRenderer;

/** Editable renderer with proper typing */
export const booleanEditable: ColumnRenderer = booleanEditableRenderer;
